import { Units } from "@stores/SettingsStore";
import { api } from "./api";

const BASE_URL = process.env.REACT_APP_API_URL ?? window.location.origin;

/**
 * Generate and download a harvest report PDF.
 * Returns a Blob that can be saved with file-saver.
 */
export const generateHarvestReport = async (
  seasonId: string,
  units: Units,
  farmId?: string
): Promise<Blob> => {
  const params = new URLSearchParams({
    seasonId,
    weight: units.weight,
    area: units.area,
    poundsPerBushel: String(units.poundsPerBushel),
    poundsPerBag: String(units.poundsPerBag),
  });
  if (farmId) params.set("farmId", farmId);

  const response = await fetch(
    `${BASE_URL}/api/v1/reports/harvest?${params.toString()}`,
    {
      method: "GET",
      credentials: "include",
    }
  );

  if (!response.ok) {
    let errorMessage = "Failed to generate report";
    try {
      const body = await response.json();
      errorMessage = body.error ?? errorMessage;
    } catch {
      // use default
    }
    throw new Error(errorMessage);
  }

  return response.blob();
};

// ── Saved statistical (correlation) reports ─────────────────

export type Layer = "yield" | "productivity" | "ndvi";

export interface ZoneStratum {
  zone: number;
  label: string;
  n: number;
  meanB: number;
  sdB: number;
}

export interface Disagreement {
  hex: string;
  a: number;
  b: number;
  aPct: number;
  bPct: number;
  kind: "underperform" | "overperform";
  lat?: number;
  lng?: number;
}

export interface CorrelationStats {
  n: number;
  pearson: number;
  spearman: number;
  pValue: number;
  significant: boolean;
  slope: number;
  intercept: number;
  r2: number;
  meanA: number;
  meanB: number;
  sdA: number;
  sdB: number;
  nonlinear: boolean;
  strength: "none" | "weak" | "moderate" | "strong" | "very strong";
  direction: "positive" | "negative" | "none";
  zones: ZoneStratum[];
  zoneGap: number | null;
  disagreements: Disagreement[];
  targetResolution?: number;
  predictorLabel?: string;
  predictorUnit?: string;
  outcomeLabel?: string;
  outcomeUnit?: string;
  yearsUsed?: number[] | null;
}

export interface ReportNarrative {
  headline: string;
  interpretation: string;
  recommendations: string[];
  caveats: string[];
}

export interface ReportSummary {
  id: string;
  type: string;
  title: string;
  fieldId: string;
  fieldName: string | null;
  seasonId: string | null;
  createdAt: string;
}

export interface FullReport {
  id: string;
  type: string;
  title: string;
  fieldId: string;
  seasonId: string | null;
  params: {
    layerA?: Layer;
    layerB?: Layer;
    ndviDate?: string | null;
    fieldName?: string;
    seasonName?: string | null;
  };
  stats: CorrelationStats;
  narrative: ReportNarrative | null;
  createdAt: string;
}

export const reportsService = {
  /** Compute + persist a correlation report; resolves to the full report. */
  createCorrelationReport: (args: {
    fieldId: string;
    layerA: Layer;
    layerB: Layer;
    seasonId?: string;
    ndviDate?: string;
    units?: Units;
  }) =>
    api.post<FullReport>("/api/v1/reports/correlation", {
      fieldId: args.fieldId,
      layerA: args.layerA,
      layerB: args.layerB,
      seasonId: args.seasonId,
      ndviDate: args.ndviDate,
      weight: args.units?.weight,
      area: args.units?.area,
      poundsPerBushel: args.units?.poundsPerBushel,
      poundsPerBag: args.units?.poundsPerBag,
    }),

  listReports: () => api.get<{ reports: ReportSummary[] }>("/api/v1/reports"),

  getReport: (id: string) => api.get<FullReport>(`/api/v1/reports/${id}`),

  deleteReport: (id: string) =>
    api.delete<{ deleted: boolean }>(`/api/v1/reports/${id}`),

  /** Cookie-authenticated direct download link for the PDF. */
  reportPdfUrl: (id: string) => `${BASE_URL}/api/v1/reports/${id}/pdf`,
};
